import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowRight,
  ArrowUp,
  CheckCircle,
  Clock,
  Layers,
  Zap,
  Shield,
  Settings,
  Wrench,
  Code,
  Users,
  MapPin,
  Lightbulb,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

// Import components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import CTABanner from './components/sections/CTABanner';
import Blog from './components/sections/Blog';
import Newsletter from './components/sections/Newsletter';
import Team from './components/sections/Team';

// Company links data
const companyLinks = {
  'Jaguar Land Rover': 'https://www.jaguarlandrover.com',
  'Ford Motor Company': 'https://www.ford.com',
  'Ford': 'https://www.ford.com',
  'Valiant': 'https://www.valiant.eu',
  'Valeo': 'https://www.valeo.com',
  'Amazon': 'https://www.amazon.com',
  'Land Rover': 'https://www.landrover.com',
  'Volkswagen': 'https://www.volkswagen.com',
  'Thyssenkrupp': 'https://www.thyssenkrupp.com',
  'VDL': 'https://www.vdlgroep.com',
  'KUKA AG': 'https://www.kuka.com',
  'KUKA': 'https://www.kuka.com'
};

// SEO constants
const SEO_SITE_NAME = "Galo Industries";
const SEO_CANONICAL_URL = "https://galo-industries.com";
const SEO_BASE_TITLE = "Industrial Automation Solutions | Zadar, Croatia";
const SEO_DESCRIPTION = "Galo Industries provides industrial automation services including software development for quality control, feeding and welding systems, plus design and implementation of industrial equipment.";

const ModernLanding = () => {
  // Refs for scrolling
  const videoRef = useRef(null);
  const statsRef = useRef(null);
  const servicesRef = useRef(null);
  const aboutRef = useRef(null);
  const projectsRef = useRef(null);

  // State management
  const [scrolled, setScrolled] = useState(false);

  // Data for the page sections
  const services = [
    {
      id: 'software',
      title: 'Efficient Software Solutions',
      shortDesc: 'Custom software for quality control, feeding systems, welding, and assembly lines.',
      description: 'We develop custom software for quality control, feeding and welding systems, laser control, and assembly lines. Simple setup or complete automation — we tailor it to your needs.',
      features: [
        { title: 'Software for individual machines or full processes', desc: 'From standalone units to complete production lines' },
        { title: 'Control interfaces tailored to your production', desc: 'User-friendly interfaces designed for your specific workflows' },
        { title: 'Integration with your existing systems', desc: 'Seamless connection with your current infrastructure' },
        { title: 'Quality control software', desc: 'Ensuring consistent product quality through automated testing' }
      ],
      icon: <Code className="w-5 h-5 sm:w-6 sm:h-6" />,
      image: "/img/code.webp",
      color: 'from-cyan-500 to-blue-600'
    },
    {
      id: 'design',
      title: 'Design, Development & Commissioning',
      shortDesc: 'From concept to implementation across the entire project lifecycle.',
      description: 'Our team can support you through the entire lifecycle — from concept to implementation. We have worked on complex systems in industries like automotive and manufacturing.',
      features: [
        { title: 'Design and documentation of custom equipment', desc: 'Comprehensive planning and technical specifications' },
        { title: 'Coordination with your engineering teams', desc: 'Collaborative approach to ensure alignment with your requirements' },
        { title: 'On-site commissioning and testing', desc: 'Expert deployment and validation in your production environment' },
        { title: 'Equipment development', desc: 'Creating specialized systems for your manufacturing needs' }
      ],
      icon: <Wrench className="w-5 h-5 sm:w-6 sm:h-6" />,
      image: "/img/robot3.jpeg",
      color: 'from-blue-500 to-cyan-600'
    },
    {
      id: 'innovation',
      title: 'Innovative Technological Solutions',
      shortDesc: 'Fresh approaches that enhance production without requiring complete overhauls.',
      description: 'Sometimes, improving your process do not mean replacing everything — it just needs a smarter setup. We offer fresh approaches that unlock better performance from your existing infrastructure.',
      features: [
        { title: 'Process upgrades using new tools', desc: 'Targeted improvements to enhance existing operations' },
        { title: 'Integration of new technologies into existing systems', desc: 'Adding modern capabilities to your current equipment' },
        { title: 'Finding efficiency without large-scale overhauls', desc: 'Optimizing performance with minimal disruption' },
        { title: 'Smart upgrades', desc: 'Strategic enhancements that deliver maximum value' }
      ],
      icon: <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6" />,
      image: "/img/robot6.jpeg",
      color: 'from-cyan-600 to-blue-700'
    }
  ];

  const projects = [
    {
      id: 1,
      title: "Jaguar Land Rover – Nitra",
      client: "Jaguar Land Rover",
      clientLink: companyLinks['Jaguar Land Rover'],
      location: "Nitra, Slovakia",
      year: "2018 – 2020",
      category: "Automotive",
      description: "Greenfield BIW & final‐assembly: underbody, framing & roof; electrical install, robot programming & PLC software; Allen-Bradley Safety PLC, ABB IRC5 & SEW drives.",
      image: "/img/robot4.jpeg",
      color: "from-cyan-500 to-blue-600",
      bgColor: "from-cyan-500/20 to-blue-500/20"
    },
    {
      id: 2,
      title: "Amazon – AOI1",
      client: "Amazon (SIT Steuerungs Technik GmbH)",
      clientLink: companyLinks['Amazon'],
      location: "Italy",
      year: "2024 – 2025",
      category: "Automation",
      description: "Greenfield AOI line: PLC software development, production optimization, IOQ & ramp-up; Siemens S1500 Safety PLC & NORD drives.",
      image: "/img/robot5.jpeg",
      color: "from-blue-500 to-cyan-600",
      bgColor: "from-blue-500/20 to-cyan-500/20"
    },
    {
      id: 3,
      title: "Ford – Cologne",
      client: "Ford Motor Company",
      clientLink: companyLinks['Ford Motor Company'],
      location: "Cologne, Germany",
      year: "2021 – 2025",
      category: "Automotive",
      description: "Greenfield & brownfield BIW & assembly: underbody, closures & framing; electrical/mechanical installs, robot programming & PLC software; Allen-Bradley Safety PLC, ABB & KUKA robots & SEW drives.",
      image: "/img/robot2.jpeg",
      color: "from-cyan-600 to-blue-700",
      bgColor: "from-cyan-500/20 to-blue-500/20"
    }
  ];

  const clients = [
    { name: 'Jaguar Land Rover', logo: '/img/Jaguar-Logo.png', link: companyLinks['Jaguar Land Rover'] },
    { name: 'Ford Motor Company', logo: '/img/ford.png', link: companyLinks['Ford Motor Company'] },
    { name: 'Valiant', logo: '/img/valiant.png', link: companyLinks['Valiant'] },
    { name: 'Valeo', logo: '/img/valeo.png', link: companyLinks['Valeo'] },
    { name: 'Amazon', logo: '/img/amazon.png', link: companyLinks['Amazon'] },
    { name: 'Land Rover', logo: '/img/JLR-Logo.png', link: companyLinks['Land Rover'] },
    { name: 'Volkswagen', logo: '/img/volkswagen-new.png', link: companyLinks['Volkswagen'] },
    { name: 'Thyssenkrupp', logo: '/img/thys.png', link: companyLinks['Thyssenkrupp'] },
    { name: 'VDL', logo: '/img/vdl.png', link: companyLinks['VDL'] },
    { name: 'KUKA AG', logo: '/img/kuka.png', link: companyLinks['KUKA AG'] }
  ];

  const values = [
    { 
      title: "Engineering Expertise", 
      description: "Deep knowledge of automation systems.", 
      icon: <Settings className="w-5 h-5 sm:w-6 sm:h-6" />,
      color: "from-cyan-500 to-blue-600"
    },
    { 
      title: "Tailored Execution", 
      description: "We adapt to your production, not the other way around.", 
      icon: <Wrench className="w-5 h-5 sm:w-6 sm:h-6" />,
      color: "from-blue-500 to-cyan-600"
    },
    { 
      title: "Real-World Thinking", 
      description: "We build systems that work on-site, not just on paper.", 
      icon: <Zap className="w-5 h-5 sm:w-6 sm:h-6" />,
      color: "from-cyan-600 to-blue-700"
    },
    { 
      title: "Continuous Curiosity", 
      description: "We apply useful technologies without the hype.", 
      icon: <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6" />,
      color: "from-blue-600 to-cyan-700"
    }
  ];

  const stats = [
    { label: "Projects", value: "50+", icon: <Layers className="w-5 h-5 sm:w-6 sm:h-6" />, description: "Successful implementations across Europe" },
    { label: "Years Experience", value: "9+", icon: <Clock className="w-5 h-5 sm:w-6 sm:h-6" />, description: "In industrial automation" },
    { label: "Industry Partners", value: "10+", icon: <Zap className="w-5 h-5 sm:w-6 sm:h-6" />, description: "Technology and manufacturing collaborations" },
    { label: "Team Members", value: "20+", icon: <Users className="w-5 h-5 sm:w-6 sm:h-6" />, description: "Dedicated automation specialists" }
  ];

  // Handle scroll event for back-to-top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Seamless mobile video autoplay - completely invisible to user
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Configure video for maximum compatibility and stealth autoplay
    const configureVideo = () => {
      // Essential mobile attributes
      video.setAttribute('webkit-playsinline', 'true');
      video.setAttribute('playsinline', 'true');
      video.setAttribute('x-webkit-airplay', 'deny');
      video.setAttribute('disablePictureInPicture', 'true');
      
      // Force muted state for autoplay compliance
      video.muted = true;
      video.defaultMuted = true;
      video.volume = 0;
      video.autoplay = true;
      video.loop = true;
      video.controls = false;
      video.preload = 'auto';
      
      // Remove any possible control visibility
      video.style.outline = 'none';
      video.style.border = 'none';
    };

    configureVideo();

    // Silent, aggressive play function
    const silentPlay = async () => {
      if (!video.paused) return;
      
      try {
        // Reset to ensure clean playback
        video.currentTime = 0;
        video.muted = true;
        video.volume = 0;
        
        const playPromise = video.play();
        if (playPromise !== undefined) {
          await playPromise;
        }
      } catch (error) {
        // Silent retry without logging to keep it invisible
        setTimeout(async () => {
          try {
            video.muted = true;
            await video.play();
          } catch (retryError) {
            // Final attempt with even more delay
            setTimeout(() => {
              video.play().catch(() => {});
            }, 1000);
          }
        }, 250);
      }
    };

    // Invisible interaction detector - captures ANY user activity
    const interactionEvents = [
      'touchstart', 'touchend', 'touchmove', 'touchcancel',
      'mousedown', 'mouseup', 'mousemove', 'click',
      'scroll', 'wheel', 'keydown', 'keyup',
      'focus', 'blur', 'resize', 'orientationchange'
    ];

    let hasInteracted = false;
    const invisiblePlayTrigger = async (event) => {
      if (hasInteracted) return;
      
      hasInteracted = true;
      await silentPlay();
      
      // Remove all listeners immediately after first interaction
      interactionEvents.forEach(eventType => {
        document.removeEventListener(eventType, invisiblePlayTrigger, true);
        window.removeEventListener(eventType, invisiblePlayTrigger, true);
      });
    };

    // Attach invisible listeners to capture any possible interaction
    interactionEvents.forEach(eventType => {
      document.addEventListener(eventType, invisiblePlayTrigger, { capture: true, passive: true });
      window.addEventListener(eventType, invisiblePlayTrigger, { capture: true, passive: true });
    });

    // Enhanced Intersection Observer for seamless video start
    const videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && video.paused && hasInteracted) {
            silentPlay();
          }
        });
      },
      { 
        threshold: [0, 0.1, 0.25], 
        rootMargin: '20px'
      }
    );

    videoObserver.observe(video);

    // Page visibility API for seamless resume
    const handleVisibilityChange = () => {
      if (!document.hidden && video.paused && hasInteracted) {
        setTimeout(silentPlay, 50);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Media loading event handlers for immediate play attempts
    const handleCanPlay = () => {
      if (hasInteracted) silentPlay();
    };

    const handleLoadedData = () => {
      // Try immediate play for browsers that allow it
      silentPlay();
      if (hasInteracted) {
        setTimeout(silentPlay, 100);
      }
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('loadedmetadata', handleLoadedData);

    // Initial silent attempts
    const initialAttempts = () => {
      silentPlay(); // Immediate attempt
      setTimeout(silentPlay, 100); // Quick retry
      setTimeout(silentPlay, 500); // Delayed retry
    };

    // Try playing as soon as possible
    if (video.readyState >= 2) {
      initialAttempts();
    } else {
      video.addEventListener('loadstart', initialAttempts, { once: true });
    }

    // Window load fallback
    if (document.readyState === 'complete') {
      setTimeout(initialAttempts, 50);
    } else {
      window.addEventListener('load', () => setTimeout(initialAttempts, 100), { once: true });
    }

    // Cleanup
    return () => {
      videoObserver.disconnect();
      
      interactionEvents.forEach(eventType => {
        document.removeEventListener(eventType, invisiblePlayTrigger, true);
        window.removeEventListener(eventType, invisiblePlayTrigger, true);
      });
      
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('loadedmetadata', handleLoadedData);
    };
  }, []);

  // Helper function to create company links
  const createCompanyLink = (companyName, className = "", children = null) => {
    const link = companyLinks[companyName];
    if (link) {
      return (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className={`hover:text-cyan-600 transition-colors duration-300 hover:underline ${className}`}
        >
          {children || companyName}
        </a>
      );
    }
    return children || companyName;
  };

  // SEO schema data
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Galo Industries",
    "url": SEO_CANONICAL_URL,
    "logo": `${SEO_CANONICAL_URL}/img/logo.png`,
    "description": "Industrial automation specialists providing efficient software solutions, design, development, implementation and commissioning of industrial equipment.",
    "slogan": "Born in Factory. Raised on Technology. Growing into Innovation.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Šibenska ulica 4B",
      "addressLocality": "Zadar",
      "addressRegion": "Zadar County",
      "postalCode": "23000",
      "addressCountry": "HR"
    },
    "foundingDate": "2016",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+385-98-199-1813",
      "contactType": "customer service",
      "email": "info@galo-industries.com"
    }
  };

  return (
    <div className="font-sans antialiased text-slate-800 overflow-x-hidden bg-white">
      {/* SEO Metadata */}
      <Helmet>
        <title>{`${SEO_SITE_NAME} | ${SEO_BASE_TITLE}`}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href={SEO_CANONICAL_URL} />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      <Header />

      {/* Scroll to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 p-2 sm:p-3 rounded-full bg-white/90 backdrop-blur-md shadow-2xl border border-slate-200/50 text-cyan-600 hover:bg-cyan-50 hover:text-cyan-700 transition-all duration-300 ${
          scrolled ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'
        }`}
        aria-label="Back to top"
      >
        <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5" />
      </button>

      {/* --- HERO SECTION --- */}
      <section className="relative min-h-screen bg-gradient-to-br from-cyan-600 via-blue-700 to-blue-900 text-white overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.3)_1px,transparent_0)] bg-[length:50px_50px]"></div>
          <div className="absolute top-1/4 left-1/4 w-48 h-48 sm:w-64 sm:h-64 lg:w-96 lg:h-96 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/3 right-1/4 w-32 h-32 sm:w-48 sm:h-48 lg:w-80 lg:h-80 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 right-1/3 w-24 h-24 sm:w-32 sm:h-32 lg:w-64 lg:h-64 bg-gradient-to-r from-cyan-400/20 to-blue-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
        </div>

        {/* Video Background - Seamless Mobile Autoplay */}
        <div className="absolute inset-0 z-0 opacity-40">
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            webkit-playsinline="true"
            x-webkit-airplay="deny"
            preload="auto"
            poster="/img/hero-bg.jpg"
            controls={false}
            disablePictureInPicture
            crossOrigin="anonymous"
            style={{ 
              objectFit: 'cover',
              width: '100%',
              height: '100%'
            }}
          >
            <source src="/img/video.mp4" type="video/mp4" />
            <source src="/img/video.webm" type="video/webm" />
            <source src="/img/video.ogv" type="video/ogg" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/60 via-blue-900/40 to-cyan-900/60"></div>
        </div>

        {/* Content Container */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 min-h-screen flex items-center justify-center relative z-10">
          <div className="max-w-5xl mx-auto text-center py-16 sm:py-20 lg:py-24 w-full">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 sm:px-6 sm:py-3 rounded-full bg-white/10 backdrop-blur-sm text-cyan-100 text-xs sm:text-sm font-semibold mb-8 sm:mb-12 shadow-2xl border border-white/20">
              <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-400 mr-2 sm:mr-3 animate-pulse"></span>
              Industrial Automation Specialists
            </div>

            {/* Main Headline */}
            <div className="mb-8 sm:mb-16">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white tracking-tight leading-tight">
                <span className="block mb-2 sm:mb-3">
                  Born in <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 via-cyan-400 to-blue-400">Factory</span>,
                </span>
                <span className="block mb-2 sm:mb-3">
                  Raised on <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-300 via-blue-400 to-cyan-400">Technology</span>,
                </span>
                <span className="block">
                  Growing into <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-300">Innovation</span>.
                </span>
              </h1>
            </div>

            {/* Subtitle */}
            <div className="relative mb-12 sm:mb-16">
              <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-cyan-100/90 mb-6 sm:mb-8 font-light max-w-4xl mx-auto leading-relaxed px-4">
                We build, design, and integrate industrial automation systems that transform efficiency and control.
              </p>
              <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-400 mx-auto rounded-full"></div>
            </div>

            {/* Scroll Indicator */}
            <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center text-cyan-200/80">
              <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-cyan-300/40 rounded-full flex justify-center pt-1.5 sm:pt-2 relative mb-2 bg-white/5 backdrop-blur-sm">
                <div className="w-0.5 sm:w-1 h-1.5 sm:h-2 bg-gradient-to-b from-cyan-300 to-blue-400 rounded-full animate-bounce"></div>
              </div>
              <span className="text-xs font-medium uppercase tracking-widest hidden sm:block">Discover More</span>
            </div>
          </div>
        </div>
      </section>

      {/* --- STATS SECTION --- */}
      <section ref={statsRef} className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-slate-50 to-cyan-50/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 sm:mb-16 lg:mb-20">
              <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-700 text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                Our Impact
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 sm:mb-6">
                Transforming <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-600">Industries</span>
              </h2>
              <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed px-4">
                From concept to execution, we help our clients automate smarter, produce more efficiently, and stay ahead of the curve.
              </p>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
              {stats.map((stat, idx) => (
                <div key={idx} className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 text-center border border-slate-200/50 shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group">
                  <div className={`p-2 sm:p-3 lg:p-4 rounded-xl sm:rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white w-max mb-3 sm:mb-4 lg:mb-6 mx-auto shadow-lg group-hover:shadow-cyan-500/25 transition-all duration-300 group-hover:scale-110`}>
                    {stat.icon}
                  </div>
                  <div className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-black text-slate-900 mb-1 sm:mb-2 lg:mb-3 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <h3 className="text-sm sm:text-lg lg:text-xl font-bold text-slate-800 mb-1 sm:mb-2 lg:mb-3">{stat.label}</h3>
                  <p className="text-xs sm:text-sm lg:text-base text-slate-600 leading-relaxed">{stat.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- CLIENTS SECTION --- */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white border-y border-slate-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-center text-center sm:text-left mb-8 sm:mb-12 lg:mb-16">
              <div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
                  Trusted by <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-600">Industry Leaders</span>
                </h2>
                <p className="text-slate-600 text-base sm:text-lg">Delivering excellence across Europe's leading manufacturers</p>
              </div>
              <a href="#projects" className="text-cyan-600 hover:text-blue-600 font-semibold flex items-center group mt-4 sm:mt-0 text-sm sm:text-base transition-colors duration-300">
                View Our Projects 
                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </a>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-cyan-50/30 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl border border-slate-200/50">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
                {clients.map((client, idx) => (
                  <a
                    key={idx}
                    href={client.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-[4/3] rounded-lg sm:rounded-xl lg:rounded-2xl overflow-hidden shadow-lg border border-slate-200/50 bg-white/80 backdrop-blur-sm p-2 sm:p-3 lg:p-4 flex items-center justify-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group hover:border-cyan-200/50 hover:bg-cyan-50/50"
                  >
                    <img
                      src={client.logo}
                      alt={`${client.name} Logo`}
                      className="max-w-full max-h-full object-contain filter grayscale brightness-110 opacity-60 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- SERVICES SECTION --- */}
      <section ref={servicesRef} id="services" className="py-16 sm:py-20 lg:py-24 xl:py-32 bg-gradient-to-br from-cyan-600 via-blue-700 to-blue-900 text-white relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_2px_2px,rgba(255,255,255,0.3)_1px,transparent_0)] bg-[length:40px_40px]"></div>
        <div className="absolute top-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-l from-cyan-500/20 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-r from-blue-500/20 to-transparent rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center lg:text-left mb-12 sm:mb-16 lg:mb-20">
              <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm text-cyan-200 text-xs sm:text-sm font-semibold mb-4 sm:mb-6 border border-white/20">
                Our Expertise
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-white mb-4 sm:mb-6 tracking-tight">
                Industrial <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 to-blue-300">Automation</span> Solutions
              </h2>
              <p className="text-lg sm:text-xl text-blue-100/90 leading-relaxed max-w-3xl mx-auto lg:mx-0 px-4 lg:px-0">
                We focus on engineering, design, consulting, and integration. Whether you need standalone software or full system setup — we provide practical, effective solutions.
              </p>
            </div>

            {/* Services Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
              {services.map((service, index) => (
                <div key={service.id} className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl overflow-hidden flex flex-col h-full group hover:bg-white/15 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl border border-white/20">
                  {/* Image Section */}
                  <div className="relative h-48 sm:h-56 lg:h-64 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                    {/* Icon */}
                    <div className={`absolute bottom-3 right-3 sm:bottom-4 sm:right-4 p-2.5 sm:p-3 lg:p-4 rounded-xl sm:rounded-2xl bg-gradient-to-br ${service.color} shadow-2xl text-white border border-white/20`}>
                      {service.icon}
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="flex-grow p-4 sm:p-6 lg:p-8 flex flex-col">
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-4">{service.title}</h3>
                    <p className="text-sm sm:text-base text-blue-100/80 mb-6 sm:mb-8 leading-relaxed">{service.description}</p>

                    {/* Feature List */}
                    <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 mt-auto">
                      {service.features.slice(0, 2).map((feature, idx) => (
                        <li key={idx} className="flex items-start rounded-lg p-2 hover:bg-white/5 transition-all duration-300">
                          <div className={`flex-shrink-0 p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-gradient-to-br ${service.color} text-white mr-3 sm:mr-4 mt-0.5 shadow-lg`}>
                            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-white mb-1 text-sm sm:text-base">{feature.title}</h4>
                            <p className="text-blue-100/70 text-xs sm:text-sm">{feature.desc}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                    
                    {/* Learn More Button */}
                    <div className="mt-auto pt-4 sm:pt-6">
                       <a
                         href={`#${service.id}`}
                         className="group flex items-center justify-between w-full px-4 sm:px-6 py-3 sm:py-4 bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/10 text-sm sm:text-base"
                       >
                         <span className="font-semibold text-white">Learn More</span>
                         <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-300 group-hover:translate-x-2 transition-transform duration-300" />
                       </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- ABOUT SECTION --- */}
      <section ref={aboutRef} id="about" className="py-16 sm:py-20 lg:py-24 xl:py-32 bg-gradient-to-br from-slate-50 to-cyan-50 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-l from-cyan-200/30 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-r from-blue-200/30 to-transparent rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12 sm:mb-16 lg:mb-20">
              <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-700 text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                Who We Are
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 mb-4 sm:mb-6 tracking-tight">
                About <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-600">Galo Industries</span>
              </h2>
              <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed px-4">
                Founded in 2016 and based in Zadar, Croatia, we bridge practical factory floor experience with cutting-edge engineering.
              </p>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-16 sm:mb-20">
              {/* Left Column - Image */}
              <div className="order-2 lg:order-1">
                <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border-4 sm:border-8 border-white group">
                  <img
                    src="/img/galo-team.webp"
                    alt="Galo Industries team working on automation project"
                    className="w-full h-auto object-cover aspect-[4/3] group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                  
                  {/* Overlay elements */}
                  <div className="absolute top-2 left-2 sm:top-4 sm:left-4 inline-flex items-center px-2 py-1 sm:px-4 sm:py-2 rounded-full bg-white/95 backdrop-blur-sm text-slate-900 text-xs sm:text-sm font-bold shadow-xl border border-slate-200/50">
                    <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-cyan-600" />
                    Zadar, Croatia
                  </div>
                  
                  <div className="absolute bottom-2 left-2 right-2 sm:bottom-4 sm:left-4 sm:right-4 text-white">
                    <div className="flex items-center gap-2 sm:gap-4 bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl p-2 sm:p-4 border border-white/20">
                      <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg sm:rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
                        <Shield className="w-4 h-4 sm:w-6 sm:h-6" />
                      </div>
                      <div>
                        <div className="font-bold text-sm sm:text-lg">Est. 2016</div>
                        <div className="text-blue-100 text-xs sm:text-sm">Automation Specialists</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Content */}
              <div className="order-1 lg:order-2">
                <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 sm:mb-6">Our Story</h3>
                <p className="text-lg sm:text-xl text-slate-700 mb-4 sm:mb-6 leading-relaxed">
                  We specialize in industrial automation — software development, equipment design, commissioning, and process optimization. Our reach extends across Europe, helping manufacturers implement robust solutions.
                </p>
                <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-8 sm:mb-10">
                  What sets us apart is our real manufacturing experience. We've worked directly on production lines for major automotive and manufacturing clients, giving us unique insights into what actually works in production environments.
                </p>

                {/* Key Stats */}
                <div className="grid grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10">
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center border border-slate-200/50 shadow-xl">
                    <div className="text-2xl sm:text-3xl font-black text-slate-900 mb-2 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Since 2016</div>
                    <div className="text-slate-700 font-medium text-sm sm:text-base">Automation Experts</div>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center border border-slate-200/50 shadow-xl">
                    <div className="text-2xl sm:text-3xl font-black text-slate-900 mb-2 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Europe-Wide</div>
                    <div className="text-slate-700 font-medium text-sm sm:text-base">Project Delivery</div>
                  </div>
                </div>

                {/* CTA Button */}
                <a
                  href="/about"
                  className="group inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg sm:rounded-xl shadow-2xl hover:shadow-cyan-500/25 hover:from-cyan-700 hover:to-blue-700 transform hover:-translate-y-1 transition-all duration-300 font-semibold text-sm sm:text-lg"
                >
                  More About Us 
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </a>
              </div>
            </div>

            {/* Core Values */}
            <div className="text-center mb-8 sm:mb-12">
              <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 sm:mb-6">Our Core Values</h3>
              <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto">
                Principles that guide our approach to automation solutions
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
              {values.map((value, idx) => (
                <div key={idx} className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 text-center border border-slate-200/50 shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group h-full flex flex-col">
                  <div className={`p-2 sm:p-3 lg:p-4 rounded-xl sm:rounded-2xl bg-gradient-to-br ${value.color} text-white w-max mb-3 sm:mb-4 lg:mb-6 mx-auto shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {value.icon}
                  </div>
                  <h4 className="font-bold text-slate-900 mb-2 sm:mb-3 text-sm sm:text-lg lg:text-xl">{value.title}</h4>
                  <p className="text-slate-600 leading-relaxed text-xs sm:text-sm lg:text-base flex-grow">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- PROJECTS SECTION --- */}
      <section ref={projectsRef} id="projects" className="py-16 sm:py-20 lg:py-24 xl:py-32 bg-gradient-to-br from-slate-900 via-cyan-900 to-blue-900 text-white relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_3px_3px,rgba(255,255,255,0.4)_2px,transparent_0)] bg-[length:60px_60px]"></div>
        <div className="absolute top-0 left-1/4 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '3s' }}></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row justify-between lg:items-end mb-12 sm:mb-16 lg:mb-20">
              <div className="max-w-3xl text-center lg:text-left mb-8 lg:mb-0">
                <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm text-cyan-200 text-xs sm:text-sm font-semibold mb-4 sm:mb-6 border border-white/20">
                  Our Work
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-white mb-4 sm:mb-6 tracking-tight">
                  Featured <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 via-blue-300 to-cyan-300">Projects</span>
                </h2>
                <p className="text-lg sm:text-xl text-blue-100/90 leading-relaxed px-4 lg:px-0">
                  We've delivered impactful solutions for leading names like {createCompanyLink('Volkswagen', 'text-cyan-300 hover:text-cyan-100')}, {createCompanyLink('Jaguar Land Rover', 'text-cyan-300 hover:text-cyan-100')}, {createCompanyLink('Ford', 'text-cyan-300 hover:text-cyan-100')}, {createCompanyLink('KUKA', 'text-cyan-300 hover:text-cyan-100')}, and {createCompanyLink('Valeo', 'text-cyan-300 hover:text-cyan-100')}. Our work is grounded in real production challenges.
                </p>
              </div>
              <a
                href="/projects"
                className="group flex-shrink-0 mx-auto lg:mx-0 inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 bg-white text-slate-900 rounded-lg sm:rounded-xl shadow-2xl font-bold hover:bg-slate-100 transform hover:-translate-y-1 transition-all duration-300 text-sm sm:text-lg"
              >
                See All Projects 
                <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:scale-110 transition-transform duration-300" />
              </a>
            </div>

            {/* Projects Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
              {projects.map((project) => (
                <a
                  key={project.id}
                  href={`/projects#project-${project.id}`}
                  className={`group rounded-2xl sm:rounded-3xl overflow-hidden bg-gradient-to-br ${project.bgColor} backdrop-blur-sm flex flex-col h-full shadow-2xl hover:shadow-cyan-500/20 transition-all duration-300 hover:-translate-y-3 border border-white/30 hover:border-cyan-300/50`}
                >
                  {/* Image */}
                  <div className="relative h-48 sm:h-56 lg:h-64 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                    
                    {/* Category Badge */}
                    <div className={`absolute top-2 left-2 sm:top-4 sm:left-4 px-2 py-1 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r ${project.color} text-white text-xs sm:text-sm font-bold shadow-xl border border-white/20`}>
                      {project.category}
                    </div>
                    
                    {/* Client Name */}
                    <div className="absolute bottom-2 left-2 right-2 sm:bottom-4 sm:left-4 sm:right-4 text-white">
                      <div className="bg-white/10 backdrop-blur-md rounded-lg sm:rounded-2xl p-2 sm:p-3 border border-white/20">
                        <a 
                          href={project.clientLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-bold text-sm sm:text-lg hover:text-cyan-300 transition-colors duration-300 inline-flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {project.client}
                          <ExternalLink className="w-3 h-3 opacity-60" />
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-4 sm:p-6 lg:p-8 flex-grow flex flex-col bg-white/5 backdrop-blur-md">
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">{project.title}</h3>
                    <p className="text-sm sm:text-base text-blue-100/90 leading-relaxed mb-4 sm:mb-6 flex-grow">
                      {project.description}
                    </p>
                    
                    {/* Footer */}
                    <div className="mt-auto pt-4 sm:pt-6 flex items-center justify-between text-xs sm:text-sm border-t border-white/20">
                      <span className="text-cyan-200 flex items-center font-medium">
                        <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
                        {project.location}
                      </span>
                      <span className="inline-flex items-center text-white font-semibold group-hover:text-cyan-200 transition-colors duration-300">
                        View Details 
                        <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 group-hover:translate-x-2 transition-transform duration-300" />
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- TEAM SECTION --- */}
      <Team />

      {/* --- CTA BANNER --- */}
      <CTABanner
        title="Ready to improve your industrial processes?"
        body="Let us help optimize your manufacturing operations."
        primary={{ label: 'Contact Us', href: '/contact' }}
        secondary={{ label: 'View Services', href: '/services' }}
      />

      {/* --- BLOG SECTION --- */}
      <Blog />

      {/* --- NEWSLETTER SECTION --- */}
      <Newsletter />

      {/* --- FOOTER --- */}
      <Footer />
    </div>
  );
};

export default ModernLanding;